import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { Plus } from "lucide-react";

import { Assessment, AssessmentsDocument } from "./assessmentsTypes";
import styles from "./CompanyAssessments.module.css";
import { addAssessment } from "./assessmentsMutations";

interface AssessmentListModalProps {
    open: boolean;
    document: AssessmentsDocument;
    mode: "edit" | "preview";
    onOpenChange: (open: boolean) => void;
    onSelect: (assessment: Assessment) => void;
    onCreate?: (assessment: Assessment) => void;
    onDraftChange?: (document: AssessmentsDocument) => void;
}

export function AssessmentListModal({
    open,
    document,
    mode,
    onOpenChange,
    onSelect,
    onCreate,
    onDraftChange
}: AssessmentListModalProps) {
    const { t } = useTranslation();

    const handleCreate = () => {
        if (!onDraftChange || !onCreate) {
            return;
        }
        const { document: next, assessment } = addAssessment(document);
        onDraftChange(next);
        onCreate(assessment);
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className={styles.dialogOverlay} />
                <Dialog.Content className={styles.dialogContent}>
                    <Dialog.Title className={styles.dialogTitle}>
                        {mode === "preview"
                            ? t("settingsPage.company.assessments.tryAssessment")
                            : t("settingsPage.company.assessments.createEditAssessment")}
                    </Dialog.Title>
                    <Dialog.Description className={styles.dialogDescription}>
                        {mode === "preview"
                            ? t("settingsPage.company.assessments.tryAssessmentDescription")
                            : t("settingsPage.company.assessments.createEditAssessmentDescription")}
                    </Dialog.Description>

                    {document.assessments.length === 0 ? (
                        <p className={styles.emptyState}>{t("settingsPage.company.assessments.empty")}</p>
                    ) : (
                        <div className={styles.list}>
                            {document.assessments.map(assessment => (
                                <button
                                    key={assessment.id}
                                    type="button"
                                    className={styles.listItem}
                                    onClick={() => onSelect(assessment)}
                                >
                                    <span className={styles.listItemTitle}>
                                        {assessment.title.trim() ||
                                            t("settingsPage.company.assessments.untitledAssessment")}
                                    </span>
                                    <span className={styles.listItemMeta}>
                                        {t("settingsPage.company.assessments.questionCount", {
                                            count: assessment.questions.length
                                        })}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {mode === "edit" && onDraftChange && onCreate ? (
                        <div className={styles.dialogActions}>
                            <div />
                            <button type="button" className={styles.dialogAddButton} onClick={handleCreate}>
                                <Plus size={14} />
                                {t("settingsPage.company.assessments.createAssessment")}
                            </button>
                        </div>
                    ) : null}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
